import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import { deleteWorkOrder } from "@/shared/repositories/WorkOrderRepository";
import { invalidateWorkOrderQueries } from "../hooks/invalidateWorkOrderQueries";
import { WorkOrderNumberField } from "./WorkOrderNumberField";

type Props = {
	workOrder: WorkOrder | null;
	open: boolean;
	onHide: (shouldUpdate?: boolean) => void;
};

export const DeleteWorkOrderModal = ({ workOrder, open, onHide }: Props) => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const mutation = useMutation({
		mutationFn: async ({
			workOrderId,
		}: {
			workOrderId: number;
			workOrderNumber: string | null;
		}) => {
			await deleteWorkOrder(workOrderId);
		},
		onSuccess: async (_response, { workOrderNumber }) => {
			await invalidateWorkOrderQueries(queryClient);
			setMessage(
				workOrderNumber
					? `Work order ${workOrderNumber} deleted successfully`
					: "Work order deleted successfully",
			);
			onHide(true);
		},
		onError: (error: Error) => setError(error.message),
	});

	return (
		<>
			<MesfModal
				id="modal-delete-work-order"
				title="DELETE WORK ORDER"
				open={open}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				fullWidth
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						if (workOrder?.WorkOrderId != null && !mutation.isLoading) {
							mutation.mutate({
								workOrderId: workOrder.WorkOrderId,
								workOrderNumber: workOrder.WorkOrderNumber,
							});
						}
					}}
				>
					<MesfModal.Content>
						<WorkOrderNumberField value={workOrder?.WorkOrderNumber} />
						<Typography sx={{ mt: 2 }}>
							Are you sure you want to delete this work order?
						</Typography>
					</MesfModal.Content>
					<MesfModal.Actions>
						<FooterModalButtons
							isSubmitLoading={mutation.isLoading}
							disabled={
								workOrder?.WorkOrderId == null || workOrder.Status !== "Pending"
							}
							onHide={() => onHide(false)}
							buttonLabel="Delete"
							buttonColor="secondary"
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
